/**
 * Razorpay Checkout — Sarathi (elder smartphone companion).
 *
 * Tiers (from sarathi/index.html pricing section):
 *   Pro        ₹499/yr  — Scam-Shield, Phone-Tutor unlimited, 3 family contacts
 *   Family     ₹2,999/yr — 5 elders covered, cross-elder rollup, SOS priority
 *   Bundle     ₹2,999/yr — All 8 Empire apps for 1 year
 *
 * Live publishable key — safe in client bundle (conscience-tax model).
 */

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => { open: () => void; on: (evt: string, cb: (data: unknown) => void) => void };
  }
}

const SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
let loadPromise: Promise<void> | null = null;

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: 'INR';
  name: string;
  description: string;
  image?: string;
  order_id?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  handler?: (response: { razorpay_payment_id: string; razorpay_order_id?: string; razorpay_signature?: string }) => void;
  modal?: { ondismiss?: () => void };
}

function loadScript(): Promise<void> {
  if (loadPromise !== null) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing !== null) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = (): void => resolve();
    s.onerror = (): void => reject(new Error('razorpay script load failed'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

export const RZP_KEY = 'rzp_live_SVzHpgz5EgB4bk';

export type Tier = 'pro_yearly' | 'family_yearly' | 'bundle_yearly';

export const PRICING: Record<Tier, { paise: number; label: string; description: string }> = {
  pro_yearly:    { paise:  49_900, label: 'Pro · Yearly',    description: 'Scam-Shield + Phone-Tutor unlimited + 3 family contacts' },
  family_yearly: { paise: 299_900, label: 'Family · Yearly', description: '5 elders covered · cross-elder rollup · SOS priority' },
  bundle_yearly: { paise: 299_900, label: 'Empire Bundle',   description: 'All 8 Empire apps · 1 year' },
};

export async function openCheckout(
  tier: Tier,
  prefill: { email?: string; contact?: string; name?: string } = {},
  onSuccess?: (paymentId: string) => void,
): Promise<void> {
  await loadScript();
  const Ctor = window.Razorpay;
  if (Ctor === undefined) throw new Error('razorpay unavailable');
  const tierCfg = PRICING[tier];
  const rz = new Ctor({
    key: RZP_KEY,
    amount: tierCfg.paise,
    currency: 'INR',
    name: 'Sarathi · सारथी',
    description: tierCfg.description,
    prefill,
    theme: { color: '#c5460e' },
    notes: { brand: 'sarathi', tier },
    handler: (response): void => {
      if (typeof onSuccess === 'function') onSuccess(response.razorpay_payment_id);
    },
  });
  rz.open();
}
